import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { packagesService } from '../services/packagesService';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  CalendarIcon,
  EyeIcon,
  CubeIcon
} from '@heroicons/react/24/outline';

const PackagesList = () => {
  const { state, dispatch } = useApp();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(null);

  useEffect(() => {
    loadPackages();
  }, []);

  const loadPackages = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      const packages = await packagesService.getPackages();
      dispatch({ type: 'SET_PACKAGES', payload: packages });
    } catch (error) {
      console.error('Error loading packages:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to load packages' });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this package?')) {
      return;
    }

    try {
      setIsDeleting(id);
      await packagesService.deletePackage(id);
      dispatch({ type: 'DELETE_PACKAGE', payload: id });
    } catch (error) {
      console.error('Error deleting package:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to delete package' });
    } finally {
      setIsDeleting(null);
    }
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  if (state.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Packages</h1>
              <p className="text-gray-600">Manage your packages and date slots</p>
            </div>
            <Link
              to="/packages/new"
              className="btn-primary flex items-center"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Add Package
            </Link>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="p-6">
        {state.error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
            <p className="text-sm text-red-600">{state.error}</p>
          </div>
        )}

        {state.packages.length === 0 ? (
          <div className="text-center py-16">
            <CubeIcon className="mx-auto h-16 w-16 text-gray-400" />
            <h3 className="mt-4 text-lg font-medium text-gray-900">No packages yet</h3>
            <p className="mt-2 text-sm text-gray-500">Get started by creating your first package.</p>
            <div className="mt-8">
              <Link
                to="/packages/new"
                className="btn-primary inline-flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Your First Package
              </Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {state.packages.map((pkg) => (
              <div key={pkg.id} className="card overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-gray-900 truncate">
                      {pkg.name}
                    </h3>
                    <div className="flex items-center space-x-2">
                      <Link
                        to={`/packages/${pkg.id}`}
                        className="text-gray-400 hover:text-gray-600"
                        title="View details"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                      <Link
                        to={`/packages/${pkg.id}/edit`}
                        className="text-gray-400 hover:text-primary-600"
                        title="Edit package"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </Link>
                      <button
                        onClick={() => handleDelete(pkg.id)}
                        disabled={isDeleting === pkg.id}
                        className="text-gray-400 hover:text-red-600 disabled:opacity-50"
                        title="Delete package"
                      >
                        {isDeleting === pkg.id ? (
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                        ) : (
                          <TrashIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>
                  
                  <p className="mt-2 text-sm text-gray-600 line-clamp-2">
                    {pkg.description}
                  </p>
                  
                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-2xl font-bold text-primary-600">
                      {formatPrice(pkg.price)}
                    </span>
                    <div className="flex items-center text-sm text-gray-500">
                      <CalendarIcon className="h-4 w-4 mr-1" />
                      {pkg.dateSlots.length} slots
                    </div>
                  </div>
                  
                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>Created: {formatDate(pkg.createdAt)}</span>
                      <span>Updated: {formatDate(pkg.updatedAt)}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default PackagesList;
